import type { MaybeValidProps } from "@core/domain/models/maybe-valid";
import type { Nullable } from "@core/domain/types/nullable.type";
import type { Unbrand } from "@core/domain/types/unbrand.type";
import type { ModelError, PartialValid } from "@iot-platform/core";
import {
    NonRecuperableModelError,
    RecuperableModelError,
    partialSafeParse,
} from "@iot-platform/core";
import type { SensorNameEnum } from "@station/domain/models/sensor-name.enum";
import { SensorNameEnumSchema } from "@station/domain/models/sensor-name.enum";
import { merge } from "es-toolkit";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

export const FormatEnum = {
    float: "float",
    int: "int",
    integer: "integer",
    string: "string",
    boolean: "boolean",
    vehicle_status: "vehicle_status",
    identity: "identity",
    direction: "direction",
    unix_timestamp: "unix_timestamp",
    float_list: "float_list",
    aqua: "aqua",
    float_div_ten: "float_div_ten",
    dosificacion_anchura: "dosificacion_anchura",
    signal_trap: "signal_trap",
} as const;
export type FormatEnum = (typeof FormatEnum)[keyof typeof FormatEnum];
const FormatEnumSchema = z.enum(FormatEnum);

export const SeriesTypeEnum = {
    spline: "spline",
    line: "line",
    bar: "bar",
    area: "area",
    column: "column",
} as const;
export type SeriesTypeEnum =
    (typeof SeriesTypeEnum)[keyof typeof SeriesTypeEnum];
const SeriesTypeEnumSchema = z.enum(SeriesTypeEnum);

const PlotBandSchema = z
    .object({
        color: z.string().trim(),
        label: z.string().trim(),
        from: z.number().nullable(),
        to: z.number().nullable(),
    })
    .readonly();
export type PlotBand = z.output<typeof PlotBandSchema>;

const EffectiveRangeSchema = z
    .object({
        min: z.number().nullable(),
        max: z.number().nullable(),
    })
    .readonly();
export type EffectiveRange = z.output<typeof EffectiveRangeSchema>;

const SensorSchema = z
    .object({
        id: z.int().positive(),
        code: SensorNameEnumSchema.nullable(),
        name: z.string().trim(),
        extendedName: z.string().trim().nullable(),
        format: FormatEnumSchema.nullable(),
        allowAccumulatedValues: z.boolean(),
        icon: z.string().trim().nullable(),
        symbol: z.string().trim().nullable(),
        graphable: z.boolean(),
        datable: z.boolean(),
        color: z.string().trim().nullable(),
        minThreshold: z.number().nullable(),
        maxThreshold: z.number().nullable(),
        goodThreshold: z.boolean(),
        showTendency: z.boolean(),
        allowReportTendency: z.boolean(),
        allowReportSum: z.boolean(),
        seriesType: SeriesTypeEnumSchema.nullable(),
        variablePlotBands: z.array(PlotBandSchema),
        customPlotBands: z.array(PlotBandSchema),
        measurementUnitId: z.number().nullable(),
        effectiveRange: EffectiveRangeSchema.nullable(),
    })
    .brand<"Sensor">();

export type SensorInputProps = z.input<typeof SensorSchema>;
/** `createWithRecovery` takes the raw backend `code`: the schema validates it and recovers an unknown one to `null`. */
type SensorRecoveryInputProps = Omit<SensorInputProps, "code"> & {
    code: Nullable<string>;
};
type SensorProps = z.output<typeof SensorSchema>;
export class Sensor {
    private static readonly HEX_BASE = 16;
    private static readonly HEX_LENGTH = 6;
    private static readonly MAX_COLOR_VALUE = 0xffffff;

    constructor(public readonly props: SensorInputProps) {}

    get id(): number {
        return this.props.id;
    }

    get code(): Nullable<SensorNameEnum> {
        return this.props.code;
    }

    get name(): string {
        return this.props.name;
    }

    get extendedName(): Nullable<string> {
        return this.props.extendedName;
    }

    get format(): Nullable<FormatEnum> {
        return this.props.format;
    }

    get allowAccumulatedValues(): boolean {
        return this.props.allowAccumulatedValues;
    }

    get icon(): Nullable<string> {
        return this.props.icon;
    }

    get symbol(): Nullable<string> {
        return this.props.symbol;
    }

    get graphable(): boolean {
        return this.props.graphable;
    }

    get datable(): boolean {
        return this.props.datable;
    }

    get color(): string {
        return this.props.color?.trim().length
            ? this.props.color
            : this.getRandomColor();
    }

    get minThreshold(): Nullable<number> {
        return this.props.minThreshold;
    }

    get maxThreshold(): Nullable<number> {
        return this.props.maxThreshold;
    }

    get goodThreshold(): boolean {
        return this.props.goodThreshold;
    }

    get showTendency(): boolean {
        return this.props.showTendency;
    }

    get allowReportTendency(): boolean {
        return this.props.allowReportTendency;
    }

    get allowReportSum(): boolean {
        return this.props.allowReportSum;
    }

    get seriesType(): Nullable<SeriesTypeEnum> {
        return this.props.seriesType;
    }

    get variablePlotBands(): PlotBand[] {
        return this.props.variablePlotBands;
    }

    get customPlotBands(): PlotBand[] {
        return this.props.customPlotBands;
    }

    get measurementUnitId(): Nullable<number> {
        return this.props.measurementUnitId;
    }

    get effectiveRange(): Nullable<EffectiveRange> {
        return this.props.effectiveRange;
    }

    static validate(
        props: SensorInputProps,
    ): Result<SensorProps, z.ZodError<SensorProps>> {
        const parsed = SensorSchema.safeParse(props);

        return parsed.success ? ok(parsed.data) : err(parsed.error);
    }

    private static partialValidate(
        props: Readonly<MaybeValidProps<SensorRecoveryInputProps>>,
    ): Result<SensorProps, PartialValid<typeof Sensor>> {
        const result = partialSafeParse(SensorSchema, props);

        return result.success
            ? ok(result.data)
            : err({
                  validProps: result.validData,
                  errors: result.error,
              });
    }

    static create(
        props: SensorInputProps,
    ): Result<Sensor, z.ZodError<SensorProps>> {
        const parsed = SensorSchema.safeParse(props);

        return parsed.success ? ok(new Sensor(parsed.data)) : err(parsed.error);
    }

    static createWithRecovery(
        props: SensorRecoveryInputProps,
    ): Result<Sensor, ModelError<typeof Sensor>> {
        return Sensor.partialValidate(props)
            .mapErr((error) => {
                if (!error.validProps.id) return new NonRecuperableModelError();

                const defaultProps: Unbrand<SensorProps> = {
                    id: error.validProps.id,
                    code: null,
                    name: "",
                    extendedName: "",
                    format: FormatEnum.float,
                    allowAccumulatedValues: false,
                    icon: "",
                    symbol: "",
                    graphable: false,
                    datable: false,
                    color: null,
                    minThreshold: null,
                    maxThreshold: null,
                    goodThreshold: false,
                    showTendency: false,
                    allowReportTendency: false,
                    allowReportSum: false,
                    seriesType: SeriesTypeEnum.spline,
                    variablePlotBands: [],
                    customPlotBands: [],
                    measurementUnitId: null,
                    effectiveRange: null,
                };

                const recoveryProps: Unbrand<SensorProps> = merge(
                    defaultProps,
                    error.validProps,
                );

                return new RecuperableModelError<typeof Sensor>(
                    new Sensor(recoveryProps),
                    error.errors,
                );
            })
            .map((validatedProps) => new Sensor(validatedProps));
    }

    private getRandomColor(): string {
        return `#${Math.floor(Math.random() * (Sensor.MAX_COLOR_VALUE + 1))
            .toString(Sensor.HEX_BASE)
            .padStart(Sensor.HEX_LENGTH, "0")
            .toUpperCase()}`;
    }
}

const SensorsSchema = z.object({
    sensors: z.array(
        z.custom<Sensor>((val) => val instanceof Sensor, {
            error: "Invalid Sensor instance",
        }),
    ),
    count: z.number().min(0),
});

export type SensorsInputProps = z.input<typeof SensorsSchema>;
export type SensorsProps = z.output<typeof SensorsSchema>;

export class Sensors {
    constructor(public readonly props: SensorsProps) {}

    get sensors(): readonly Readonly<Sensor>[] {
        return this.props.sensors;
    }

    get count(): number {
        return this.props.count;
    }

    static empty(): Sensors {
        return Sensors.create({
            sensors: [],
            count: 0,
        })._unsafeUnwrap();
    }

    static create(
        props: Readonly<SensorsInputProps>,
    ): Result<Sensors, z.ZodError<SensorsProps>> {
        return Sensors.validate(props).map(
            (validatedProps) => new Sensors(validatedProps),
        );
    }

    static validate(
        props: Readonly<MaybeValidProps<SensorsInputProps>>,
    ): Result<SensorsProps, z.ZodError<SensorsProps>> {
        const result = SensorsSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }
}
